import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    // Run on both Server and Browser
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateMetaTags();
    });

    this.translate.onLangChange.subscribe(() => {
      this.updateMetaTags();
    });
  }

  public updateMetaTags(): void {
    // Determine the base URL dynamically
    const contentBaseUrl = this.getContentBaseUrl();

    // Default values
    const title = 'EquiFoot - ' + this.translate.instant('APP_DESCRIPTION');
    const description = this.translate.instant('APP_DESCRIPTION');

    // Construct absolute URL
    // Handle the case where router.url might be just '/' or '/path'
    const path = this.router.url === '/' ? '' : this.router.url;
    const url = `${contentBaseUrl}${path}`;

    this.titleService.setTitle(title);

    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });

    // Open Graph
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:url', content: url });
    this.metaService.updateTag({
      property: 'og:image',
      content: `${contentBaseUrl}/img/logo-equifoot.png`
    });

    // Twitter Card
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    this.metaService.updateTag({
      name: 'twitter:image',
      content: `${contentBaseUrl}/img/logo-equifoot.png`
    });

    // Canonical
    this.createLinkForCanonicalURL(url);

    // Structured Data (JSON-LD)
    this.setStructuredData(contentBaseUrl);
  }

  private getContentBaseUrl(): string {
    // Fallback to production if something goes wrong or on SSR with no host info
    let protocol = 'https:';
    let hostname = 'equipfoot.com';

    if (this.document && this.document.location) {
      // Correctly capture the current hostname (dev or prod)
      if (
        this.document.location.hostname.includes('equipfoot.com') ||
        this.document.location.hostname.includes('localhost')
      ) {
        hostname = this.document.location.hostname;
      }

      // Preserve protocol (http for localhost)
      if (this.document.location.protocol) {
        protocol = this.document.location.protocol;
      }
    }

    // Construct origin, ensure no trailing slash
    const port = this.document.location.port ? `:${this.document.location.port}` : '';
    return `${protocol}//${hostname}${port}`;
  }

  private setStructuredData(baseUrl: string): void {
    const scriptId = 'structured-data';
    let script = this.document.getElementById(scriptId) as HTMLScriptElement;

    if (!script) {
      script = this.document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'EquiFoot',
      applicationCategory: 'SportsApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR'
      },
      description: this.translate.instant('APP_DESCRIPTION'),
      image: `${baseUrl}/img/logo-equifoot.png`,
      author: {
        '@type': 'Person',
        name: 'EquiFoot Team'
      }
    };

    script.text = JSON.stringify(structuredData);
  }

  private createLinkForCanonicalURL(url: string) {
    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url.split('?')[0]);
  }
}
